import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../helpers/api";
import Select from "react-select";
import { Row, Col, Input, Spinner, Modal } from "reactstrap";
import { currencies } from "../constants/utils";
import FeatherIcon from "feather-icons-react";

/**
 * Shared SparePartsForm component following the WorkOrderForm pattern.
 * Renders all spare part form fields. Does NOT handle submit logic,
 * modal wrapping, or validation — the parent does those.
 */
const SparePartsForm = ({
  formData,
  onChange,
  errors = {},
  apiError,
  apiWarning,
  vendors = [],
  enumData = {},
  organizationId,
  manufacturers = [],
  locations = [],
  assetTypes = [],
  sparePartTypes = [],
  isEditMode,
}) => {
  const { t } = useTranslation();

  // Internal state for asset search
  const [assetSearchTerm, setAssetSearchTerm] = useState("");
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetFilterModal, setAssetFilterModal] = useState(false);
  const [assetFilters, setAssetFilters] = useState({
    assetTypeId: '',
    location: '',
    manufacturer: ''
  });

  // Upload states
  const [imageUploading, setImageUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);

  // Select style helpers (matching WorkOrderForm)
  const selectStyles = (hasError) => ({
    control: (base, state) => ({
      ...base,
      borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
      boxShadow: hasError
        ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
        : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
      '&:hover': { borderColor: '#86b7fe' }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': { backgroundColor: '#2e669a' }
    })
  });

  // Fetch assets on search/filter change
  const fetchAssets = useCallback(async () => {
    try {
      const params = {
        organizationId,
        count: 5,
        search: assetSearchTerm,
        currentLocation: assetFilters.location,
        manufacturer: assetFilters.manufacturer,
        hierarchy: false
      };
      if (assetFilters.assetTypeId) {
        params.assetTypeId = assetFilters.assetTypeId;
      }
      const { data } = await api.get(`asset/all`, { params });
      setAssets(data?.response[0]?.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, [assetSearchTerm, assetFilters, organizationId]);

  useEffect(() => {
    if (assetSearchTerm.length > 0) {
      const debounce = setTimeout(fetchAssets, 300);
      return () => clearTimeout(debounce);
    }
  }, [assetSearchTerm, fetchAssets]);

  useEffect(() => {
    if (assetFilters.location || assetFilters.manufacturer || assetFilters.assetTypeId) {
      fetchAssets();
    }
  }, [assetFilters, fetchAssets]);

  const handleAssetSearchChange = (e) => {
    const value = e.target.value;
    setAssetSearchTerm(value);
    setShowAssetDropdown(value.length > 0);
  };

  const handleAssetSelect = (asset) => {
    const currentAssets = formData?.selectedAssets || [];
    onChange({ selectedAssets: [...currentAssets, asset] });
    setAssetSearchTerm("");
    setShowAssetDropdown(false);
  };

  const handleRemoveAsset = (index) => {
    const updatedAssets = (formData?.selectedAssets || []).filter((_, i) => i !== index);
    onChange({ selectedAssets: updatedAssets });
  };

  return (
    <>
      {/* API Errors/Warnings */}
      {apiError && (
        <div className="alert alert-danger mb-4">{apiError}</div>
      )}
      {apiWarning && (
        <div className="alert alert-warning mb-4">{apiWarning}</div>
      )}

      {/* Name (required) */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="name" className="form-label fw-medium">
              {t('shared.name', 'Name')} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="name"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              placeholder={t('business.spare_parts.enter_name', 'Enter spare part name')}
              value={formData?.name || ''}
              onChange={(e) => onChange({ name: e.target.value })}
            />
            {errors.name && <small className="text-danger">{errors.name}</small>}
          </div>
        </Col>
      </Row>

      {/* Type */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="sparePartTypeId" className="form-label fw-medium">
              {t('shared.type', 'Type')}
            </label>
            <Select
              id="sparePartTypeId"
              options={sparePartTypes.map((spt) => ({
                value: spt.id,
                label: spt.name
              }))}
              value={
                sparePartTypes.find(spt => spt.id === formData?.sparePartTypeId)
                  ? { value: formData.sparePartTypeId, label: sparePartTypes.find(spt => spt.id === formData.sparePartTypeId)?.name }
                  : null
              }
              onChange={(selected) => onChange({ sparePartTypeId: selected?.value || null })}
              isClearable
              isSearchable
              placeholder={t('shared.select_spare_part_type', 'Select Type')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
              noOptionsMessage={() => t('shared.no_spare_part_types_found', 'No spare part types found')}
            />
          </div>
        </Col>
      </Row>

      {/* Assets */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="assetSearch" className="form-label fw-medium">
              {t('shared.assets', 'Assets')}
            </label>
            <div className="d-flex gap-2">
              <div className="flex-grow-1 position-relative">
                <Input
                  type="text"
                  id="assetSearch"
                  placeholder={t('shared.type_to_search_assets', 'Type to search assets...')}
                  value={assetSearchTerm}
                  onChange={handleAssetSearchChange}
                  onFocus={() => {
                    if (assetSearchTerm.length > 0) {
                      setShowAssetDropdown(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowAssetDropdown(false), 200);
                  }}
                />
                {showAssetDropdown && assets.length > 0 && (
                  <div
                    className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                    style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto', top: '100%' }}
                  >
                    {assets
                      .filter(asset => !(formData?.selectedAssets || []).some(selected => selected.id === asset.id))
                      .map((asset) => (
                        <div
                          key={asset.id}
                          className="p-2 border-bottom d-flex align-items-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleAssetSelect(asset)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div className="flex-grow-1">
                            <div className="fw-medium text-dark">{asset.assetName}</div>
                            <small className="text-muted">{asset.serialNumber}</small>
                          </div>
                          <FeatherIcon icon="chevron-right" size={16} className="text-muted" />
                        </div>
                      ))}
                  </div>
                )}
                {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                  <div
                    className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                    style={{ zIndex: 1050, top: '100%' }}
                  >
                    {t('shared.no_assets_found_matching', 'No assets found matching "{{searchTerm}}"', { searchTerm: assetSearchTerm })}
                  </div>
                )}
              </div>
              <div className="align-self-end">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setAssetFilterModal(true)}
                  title={t('shared.filter_assets', 'Filter assets')}
                  style={{ height: '38px' }}
                >
                  <FeatherIcon icon="filter" size={16} />
                </button>
              </div>
            </div>
            {/* Selected assets as badges */}
            {(formData?.selectedAssets || []).length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {(formData?.selectedAssets || []).map((asset, index) => (
                  <span
                    key={asset.id || index}
                    className="badge bg-primary d-flex align-items-center gap-1"
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                  >
                    <FeatherIcon icon="package" size={12} />
                    {asset.name || asset.assetName}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ fontSize: '8px' }}
                      onClick={() => handleRemoveAsset(index)}
                      aria-label="Remove"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Location */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="preferredStorageLocation" className="form-label fw-medium">
              {t('shared.location', 'Location')}
            </label>
            <Select
              id="preferredStorageLocation"
              options={locations.map(loc => ({
                value: loc.locationName || loc.name,
                label: loc.locationName || loc.name
              }))}
              value={
                formData?.preferredStorageLocation
                  ? { value: formData.preferredStorageLocation, label: formData.preferredStorageLocation }
                  : null
              }
              onChange={(selected) => onChange({ preferredStorageLocation: selected?.value || '' })}
              isClearable
              isSearchable
              placeholder={t('shared.select_location', 'Select Location')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
              noOptionsMessage={() => t('business.locations.no_locations', 'No locations found')}
            />
          </div>
        </Col>
      </Row>

      {/* Vendor */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="vendorId" className="form-label fw-medium">
              {t('shared.vendor', 'Vendor')}
            </label>
            <Select
              id="vendorId"
              options={vendors?.map((v) => ({
                value: v.id,
                label: v.vendorName || v.name
              })) || []}
              value={
                vendors?.find(v => v.id === formData?.vendorId)
                  ? { value: formData.vendorId, label: vendors.find(v => v.id === formData.vendorId)?.vendorName || vendors.find(v => v.id === formData.vendorId)?.name }
                  : null
              }
              onChange={(selected) => onChange({ vendorId: selected?.value || '' })}
              isClearable
              isSearchable
              placeholder={t('shared.select_vendor', 'Select Vendor')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
              noOptionsMessage={() => t('shared.no_vendors_found', 'No vendors found')}
            />
          </div>
        </Col>
      </Row>

      {/* Description */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="description" className="form-label fw-medium">
              {t('shared.description', 'Description')}
            </label>
            <textarea
              id="description"
              className="form-control"
              rows={3}
              placeholder={t('shared.enter_description', 'Enter description')}
              value={formData?.description || ''}
              onChange={(e) => onChange({ description: e.target.value })}
            />
          </div>
        </Col>
      </Row>

      {/* Manufacturer Name */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="manufacturerName" className="form-label fw-medium">
              {t('shared.manufacturer', 'Manufacturer Name')}
            </label>
            <input
              type="text"
              id="manufacturerName"
              className="form-control"
              placeholder={t('shared.enter_manufacturer_name', 'Enter manufacturer name')}
              value={formData?.manufacturerName || ''}
              onChange={(e) => onChange({ manufacturerName: e.target.value })}
            />
          </div>
        </Col>
      </Row>

      {/* Part Number */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="manufacturerPartNumber" className="form-label fw-medium">
              {t('shared.part_number', 'Part Number')}
            </label>
            <input
              type="text"
              id="manufacturerPartNumber"
              className="form-control"
              placeholder={t('business.spare_parts.enter_part_number', 'Enter part number')}
              value={formData?.manufacturerPartNumber || ''}
              onChange={(e) => onChange({ manufacturerPartNumber: e.target.value })}
            />
          </div>
        </Col>
      </Row>

      {/* Criticality */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="criticality" className="form-label fw-medium">
              {t('shared.criticality', 'Criticality')}
            </label>
            <Select
              id="criticality"
              options={(enumData?.criticality || []).map((c) => {
                const val = typeof c === 'string' ? c : c.name || c.value;
                return { value: val, label: t(`shared.${val.toLowerCase()}`, val) };
              })}
              value={
                formData?.criticality
                  ? { value: formData.criticality, label: t(`shared.${formData.criticality.toLowerCase()}`, formData.criticality) }
                  : null
              }
              onChange={(selected) => onChange({ criticality: selected?.value || '' })}
              isClearable
              isSearchable
              placeholder={t('shared.select_criticality', 'Select Criticality')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
            />
          </div>
        </Col>
      </Row>

      {/* Units in Stock */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="quantity" className="form-label fw-medium">
              {t('business.spare_parts.units_in_stock', 'Units in Stock')}
            </label>
            <input
              type="number"
              id="quantity"
              className={`form-control ${isEditMode ? 'bg-light' : ''}`}
              min="0"
              placeholder={t('business.spare_parts.enter_units_in_stock', 'Enter initial stock quantity')}
              value={formData?.quantity ?? ''}
              onChange={(e) => onChange({ quantity: e.target.value })}
              disabled={isEditMode}
            />
            {isEditMode && (
              <small className="text-muted">{t('business.spare_parts.use_restock_to_change', 'Use the Restock function to adjust stock levels')}</small>
            )}
          </div>
        </Col>
      </Row>

      {/* Minimum Quantity */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="minimumStockLevel" className="form-label fw-medium">
              {t('business.spare_parts.minimum_quantity', 'Minimum Quantity')}
            </label>
            <input
              type="number"
              id="minimumStockLevel"
              className="form-control"
              min="0"
              placeholder={t('business.spare_parts.enter_minimum_quantity', 'Enter minimum quantity')}
              value={formData?.minimumStockLevel ?? ''}
              onChange={(e) => onChange({ minimumStockLevel: e.target.value })}
            />
          </div>
        </Col>
      </Row>

      {/* Average Unit Cost & Currency */}
      <Row className="mb-4">
        <Col md={6}>
          <div className="form-group">
            <label htmlFor="averageUnitCost" className="form-label fw-medium">
              {t('business.spare_parts.average_unit_cost', 'Average Unit Cost')}
            </label>
            <input
              type="number"
              id="averageUnitCost"
              className="form-control"
              min="0"
              step="0.01"
              placeholder={t('business.spare_parts.enter_average_unit_cost', 'Enter average unit cost')}
              value={formData?.averageUnitCost ?? ''}
              onChange={(e) => onChange({ averageUnitCost: e.target.value })}
            />
          </div>
        </Col>
        <Col md={6}>
          <div className="form-group">
            <label htmlFor="currency" className="form-label fw-medium">
              {t('shared.currency', 'Currency')}
            </label>
            {(() => {
              // Currency is org-level. Pull from the part's parent org on edit, fall back to the
              // active-org currency stored on authUser when creating. Disabled so per-part
              // overrides aren't possible — change it under Organization Settings.
              let activeOrgCurrency = 'USD';
              try {
                const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
                activeOrgCurrency = authUser?.activeOrganizationCurrency || 'USD';
              } catch { /* localStorage failure falls through to USD */ }
              const orgCurrency = formData?.organization?.currency || activeOrgCurrency;
              return (
                <Select
                  id="currency"
                  options={Object.entries(currencies(t)).map(([code, label]) => ({
                    value: code,
                    label,
                  }))}
                  value={{ value: orgCurrency, label: currencies(t)[orgCurrency] || orgCurrency }}
                  isDisabled
                  classNamePrefix="react-select"
                />
              );
            })()}
            <small className="text-muted">
              {t(
                'shared.currency_org_locked_hint',
                'Set under Organization Settings — applies to every asset, part, and tool in the organization.'
              )}
            </small>
          </div>
        </Col>
      </Row>

      {/* Image Upload */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label className="form-label fw-medium">{t('shared.images', 'Images')}</label>
            <div
              className="border rounded p-4 text-center"
              style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
            >
              <input
                type="file"
                id="sparePartImageUpload"
                className="d-none"
                accept="image/*"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 0) {
                    setImageUploading(true);
                    try {
                      const fd = new FormData();
                      files.forEach(file => fd.append('files', file));
                      const { data } = await api.post('files/img-upload', fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      const newUrls = (data.response || []).map(item => item.publicUrl);
                      onChange({ imageUrls: [...(formData?.imageUrls || []), ...newUrls] });
                    } catch (error) {
                      console.error('Error uploading images:', error);
                    } finally {
                      setImageUploading(false);
                    }
                  }
                }}
                disabled={imageUploading}
              />
              <label
                htmlFor="sparePartImageUpload"
                className={`btn btn-outline-primary mb-2 ${imageUploading ? 'disabled' : ''}`}
                style={{ cursor: imageUploading ? 'not-allowed' : 'pointer' }}
              >
                {imageUploading ? (
                  <><Spinner size="sm" className="me-2" />{t('shared.uploading', 'Uploading...')}</>
                ) : (
                  <><FeatherIcon icon="upload" size={16} className="me-2" />{t('shared.upload_images', 'Upload Images')}</>
                )}
              </label>
              <p className="text-muted small mb-0">
                {imageUploading
                  ? t('shared.wait_uploading_images', 'Please wait while images are being uploaded...')
                  : t('shared.click_to_select_images', 'Click to select images to upload')}
              </p>
            </div>
            {formData?.imageUrls?.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                {formData.imageUrls.map((url, index) => (
                  <div key={index} className="position-relative rounded overflow-hidden" style={{ width: '100px', height: '100px' }}>
                    <img src={url} alt={`Upload ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm position-absolute d-flex align-items-center justify-content-center"
                      style={{ top: '4px', right: '4px', width: '24px', height: '24px', padding: 0, borderRadius: '50%' }}
                      onClick={() => {
                        const newUrls = formData.imageUrls.filter((_, i) => i !== index);
                        onChange({ imageUrls: newUrls });
                      }}
                    >
                      <FeatherIcon icon="x" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Document Upload */}
      <Row className="mb-4">
        <Col md={12}>
          <div className="form-group">
            <label className="form-label fw-medium">{t('shared.files', 'Files')}</label>
            <div
              className="border rounded p-4 text-center"
              style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
            >
              <input
                type="file"
                id="sparePartDocUpload"
                className="d-none"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 0) {
                    setFileUploading(true);
                    try {
                      const fd = new FormData();
                      files.forEach(file => fd.append('files', file));
                      const { data } = await api.post('files/pdf-upload', fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      const newUrls = (data.response || []).map(item => item.publicUrl);
                      onChange({ documentUrls: [...(formData?.documentUrls || []), ...newUrls] });
                    } catch (error) {
                      console.error('Error uploading files:', error);
                    } finally {
                      setFileUploading(false);
                    }
                  }
                }}
                disabled={fileUploading}
              />
              <label
                htmlFor="sparePartDocUpload"
                className={`btn btn-outline-secondary mb-2 ${fileUploading ? 'disabled' : ''}`}
                style={{ cursor: fileUploading ? 'not-allowed' : 'pointer' }}
              >
                {fileUploading ? (
                  <><Spinner size="sm" className="me-2" />{t('shared.uploading', 'Uploading...')}</>
                ) : (
                  <><FeatherIcon icon="paperclip" size={16} className="me-2" />{t('shared.attach_files', 'Attach Files')}</>
                )}
              </label>
              <p className="text-muted small mb-0">
                {fileUploading
                  ? t('shared.wait_uploading_files', 'Please wait while files are being uploaded...')
                  : t('shared.click_to_select_files', 'Click to select files to upload')}
              </p>
            </div>
            {formData?.documentUrls?.length > 0 && (
              <div className="mt-3">
                {formData.documentUrls.map((url, index) => (
                  <div key={index} className="d-flex align-items-center justify-content-between bg-light rounded px-3 py-2 mb-2">
                    <div className="d-flex align-items-center">
                      <FeatherIcon icon="file-text" size={18} className="text-primary me-2" />
                      <span className="text-truncate" style={{ maxWidth: '300px' }}>{url.split('/').pop()}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => {
                        const newUrls = formData.documentUrls.filter((_, i) => i !== index);
                        onChange({ documentUrls: newUrls });
                      }}
                    >
                      <FeatherIcon icon="trash-2" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Asset Filter Modal */}
      <Modal isOpen={assetFilterModal} toggle={() => setAssetFilterModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.filter_assets', 'Filter Assets')}</h5>
          <button type="button" className="btn-close" onClick={() => setAssetFilterModal(false)} />
        </div>
        <div className="modal-body">
          <div className="mb-3">
            <label className="form-label">{t('shared.asset_type', 'Asset Type')}</label>
            <Select
              options={assetTypes.map(at => ({ value: at.id, label: at.name }))}
              value={assetFilters.assetTypeId ? assetTypes.filter(at => at.id === assetFilters.assetTypeId).map(at => ({ value: at.id, label: at.name }))[0] : null}
              onChange={(selected) => setAssetFilters(prev => ({ ...prev, assetTypeId: selected?.value || '' }))}
              isClearable
              placeholder={t('shared.select_asset_type', 'Select asset type')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">{t('shared.location', 'Location')}</label>
            <Select
              options={locations.map(loc => ({ value: loc.locationName || loc.name, label: loc.locationName || loc.name }))}
              value={assetFilters.location ? { value: assetFilters.location, label: assetFilters.location } : null}
              onChange={(selected) => setAssetFilters(prev => ({ ...prev, location: selected?.value || '' }))}
              isClearable
              placeholder={t('shared.select_location', 'Select location')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">{t('shared.manufacturer', 'Manufacturer')}</label>
            <Select
              options={manufacturers.map(m => ({ value: m, label: m }))}
              value={assetFilters.manufacturer ? { value: assetFilters.manufacturer, label: assetFilters.manufacturer } : null}
              onChange={(selected) => setAssetFilters(prev => ({ ...prev, manufacturer: selected?.value || '' }))}
              isClearable
              placeholder={t('shared.select_manufacturer', 'Select manufacturer')}
              classNamePrefix="react-select"
              styles={selectStyles(false)}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              setAssetFilters({ assetTypeId: '', location: '', manufacturer: '' });
              setAssetFilterModal(false);
            }}
          >
            {t('shared.clear_filters', 'Clear Filters')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setAssetFilterModal(false)}
          >
            {t('shared.apply', 'Apply')}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default SparePartsForm;
